import React, { useState } from 'react'
import { BiPlug, BiChevronDown } from 'react-icons/bi'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug, faTimesCircle, faFileImport } from '@fortawesome/free-solid-svg-icons';
import UninstallModal from './UninstallModal'
import StripeUninstallModal from '../channels/stripe/StripeUninstallModal'
import SlackUninstallModal from '../channels/slack/SlackUninstallModal'
import TeamsUninstallModal from '../channels/teams/TeamsUninstallModal'
import WooCommerceUninstallModal from '../channels/woocommerce/WooCommerceUninstallModal'

/** Integrations whose disconnect needs a channel-specific confirmation + endpoint. */
const CHANNEL_UNINSTALL_MODALS = {
  stripe: StripeUninstallModal,
  slack: SlackUninstallModal,
  teams: TeamsUninstallModal,
  woocommerce: WooCommerceUninstallModal,
  wordpress: WooCommerceUninstallModal,
}

export default function ConnectButton({
  onClick = () => {},
  channelId = null,
  uninstallEnabled = false,
  integration = null,
  connected = false,
  onBackfillClick = null,
  showBackfillOption = false,
  onImportOrdersClick = null,
  showImportOrdersOption = false,
  canConnect = true,
  onConfigureClick = null,
  /** InkFrog: `{ onListings, onOrders }` — single Upload control opens a menu (connected or not). */
  inkfrogDualUploadDisconnected = null,
} = {}) {
  const [isOpen, setIsOpen] = useState(false)
  const [inkfrogUploadOpen, setInkfrogUploadOpen] = useState(false)
  let buttonName = connected ? 'Configure' : 'Connect'

  const toggleDropdown = () => {
    setIsOpen(!isOpen)
  }

  const toggleInkfrogUploadMenu = () => {
    setInkfrogUploadOpen((v) => !v)
  }

  /**
   *
   * Handle modal
   *
   */
  const [visible, setVisible] = useState(false)
  const closeModal = () => setVisible(false)
  const showModal = () => setVisible(true)

  const baseButtonClassName =
    'tw-text-sm tw-cursor-pointer tw-rounded-md tw-border tw-border-slate-300 hover:tw-border-blue-600 hover:tw-bg-blue-600 hover:tw-text-white tw-transition-colors tw-duration-150 tw-shadow-sm tw-flex tw-items-center tw-space-x-2.5 tw-px-2.5 tw-py-1'

  const ChannelUninstallModal = CHANNEL_UNINSTALL_MODALS[integration?.id] || UninstallModal

  if (channelId === 'inkfrog_listings' && !uninstallEnabled && inkfrogDualUploadDisconnected) {
    const { onListings, onOrders } = inkfrogDualUploadDisconnected
    return (
      <div className="tw-relative tw-inline-block tw-text-left">
        <div
          className={baseButtonClassName}
          onClick={toggleInkfrogUploadMenu}
          aria-expanded={inkfrogUploadOpen}
          aria-haspopup="menu"
        >
          <BiPlug />
          <div>Upload</div>
          <BiChevronDown className={inkfrogUploadOpen ? 'tw-rotate-180 tw-transition-transform' : 'tw-transition-transform'} />
        </div>
        {inkfrogUploadOpen && (
          <div
            className="tw-absolute tw-right-0 tw-z-10 tw-mt-2 tw-min-w-[13.5rem] tw-rounded-md tw-bg-white tw-py-1 tw-shadow-lg tw-ring-1 tw-ring-black tw-ring-opacity-5"
            role="menu"
          >
            <button
              type="button"
              role="menuitem"
              className="tw-block tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900"
              onClick={() => {
                onListings()
                setInkfrogUploadOpen(false)
              }}
            >
              Upload listings CSV
            </button>
            <button
              type="button"
              role="menuitem"
              className="tw-block tw-w-full tw-px-4 tw-py-2 tw-text-left tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900"
              onClick={() => {
                onOrders()
                setInkfrogUploadOpen(false)
              }}
            >
              Upload orders CSV
            </button>
          </div>
        )}
      </div>
    )
  }

  const isCsvUploadIntegration =
    channelId === 'upload' || (channelId === 'inkfrog_listings' && !inkfrogDualUploadDisconnected)
  if (isCsvUploadIntegration && !uninstallEnabled) {
    return (
      <div className={baseButtonClassName} onClick={onClick}>
        <BiPlug />
        <div>Upload</div>
      </div>
    )
  }

  if (!connected && !uninstallEnabled) {
    return (
      <div
        className={baseButtonClassName}
        onClick={onClick}
      >
        <BiPlug />
        <div>{buttonName}</div>
      </div>
    )
  }

  return (
    <div className="tw-relative tw-inline-block tw-text-left">
      <div
        className={baseButtonClassName}
        onClick={toggleDropdown}
      >
        {!connected && <BiPlug />}
        <div>{buttonName}</div>
        <BiChevronDown />
      </div>
      {isOpen && (
        <div className="tw-absolute tw-right-0 tw-mt-2 tw-w-56 tw-rounded-md tw-shadow-lg tw-bg-white tw-ring-1 tw-ring-black tw-ring-opacity-5 focus:tw-outline-none">
          <div className="tw-py-1" role="menu" aria-orientation="vertical" aria-labelledby="options-menu">
            {canConnect && (
              <button
                onClick={() => {
                  onClick();
                  setIsOpen(false);
                }}
                className="tw-block tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900 tw-w-full tw-text-left"
                role="menuitem"
              >
                <FontAwesomeIcon icon={faPlug} className="tw-mr-2" />
                {connected && channelId === 'amazon-ads' ? 'Connect another account' : 'Connect'}
              </button>
            )}
            {connected && showImportOrdersOption && (
              <button
                onClick={() => {
                  onImportOrdersClick?.();
                  setIsOpen(false);
                }}
                className="tw-block tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900 tw-w-full tw-text-left"
                role="menuitem"
              >
                <FontAwesomeIcon icon={faFileImport} className="tw-mr-2" />
                Import historical orders
              </button>
            )}
            {uninstallEnabled && (
              <button
                onClick={() => {
                  showModal();
                  setIsOpen(false);
                }}
                className="tw-block tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-100 hover:tw-text-gray-900 tw-w-full tw-text-left"
                role="menuitem"
              >
                <FontAwesomeIcon icon={faTimesCircle} className="tw-mr-2" />
                Disconnect
              </button>
            )}
          </div>
        </div>
      )}
      <ChannelUninstallModal visible={visible} closeModal={closeModal} integration={integration} />
    </div>
  )
}
